import { businessMerchantKey } from './reporting/business-expense-merchant';
import type { CategoryWithTree } from './reporting/business-expense-scope';
import { resolveBusinessCategoryScope } from './reporting/business-expense-scope';
import { isExpenseOutflow } from './reporting/transaction-amount-utils';
import type { Category } from '../types/category.types';
import type {
  BusinessClaimsCoverage,
  BusinessClaimsReceiptStatus,
  BusinessClaimsVendorGroup,
} from '../types/business-claims.types';
import type { Transaction, TransactionGstStatus } from '../types/transaction.types';

export function categoriesToBusinessTree(categories: Category[]): CategoryWithTree[] {
  return categories.map((c) => ({
    id: c.id,
    name: c.name,
    parent_id: c.parentId ?? null,
    slug: c.slug ?? null,
    deductible: c.deductible,
    gst_applicable: c.gstApplicable,
  }));
}

export function resolveBusinessClaimsCategoryIds(categories: Category[]): string[] {
  const scope = resolveBusinessCategoryScope(categoriesToBusinessTree(categories));
  return scope ? Array.from(scope) : [];
}

export function isTransactionInBusinessClaimsScope(
  categoryId: string | undefined,
  scope: Set<string> | null
): boolean {
  if (!categoryId || !scope?.size) return false;
  return scope.has(categoryId);
}

function receiptStatusForTransactions(rows: Transaction[]): BusinessClaimsReceiptStatus {
  if (!rows.length) return 'none';
  const withReceipt = rows.filter((r) => r.receiptAttached).length;
  if (withReceipt === 0) return 'none';
  if (withReceipt === rows.length) return 'all';
  return 'partial';
}

function dominantBusinessPercent(rows: Transaction[]): number | null {
  const values = rows.map((r) => r.businessUsePercent).filter((v): v is number => v != null);
  if (!values.length) return null;
  const unique = new Set(values);
  if (unique.size === 1) return values[0] ?? null;
  const counts = new Map<number, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

function dominantGstStatus(rows: Transaction[]): TransactionGstStatus | 'mixed' {
  const values = rows.map((r) => r.gstStatus ?? 'unknown');
  const unique = new Set(values);
  if (unique.size <= 1) return values[0] ?? 'unknown';
  return 'mixed';
}

function coverageForTransactions(rows: Transaction[]): BusinessClaimsCoverage {
  const withPct = rows.filter((r) => r.businessUsePercent != null).length;
  if (withPct === 0) return 'unset';
  if (withPct === rows.length) return 'complete';
  return 'partial';
}

export function buildBusinessClaimsVendorGroups(transactions: Transaction[]): BusinessClaimsVendorGroup[] {
  const byVendor = new Map<string, Transaction[]>();

  for (const tx of transactions) {
    if (!isExpenseOutflow(tx)) continue;
    const key = businessMerchantKey(tx.description, tx.merchantName);
    const list = byVendor.get(key) ?? [];
    list.push(tx);
    byVendor.set(key, list);
  }

  return Array.from(byVendor.entries())
    .map(([vendorLabel, rows]) => {
      const totalAmountAud = rows.reduce((s, r) => s + Math.abs(r.amount), 0);
      return {
        id: vendorLabel,
        vendorLabel,
        transactionIds: rows.map((r) => r.id),
        transactionCount: rows.length,
        totalAmountAud,
        businessUsePercent: dominantBusinessPercent(rows),
        gstStatus: dominantGstStatus(rows),
        receiptStatus: receiptStatusForTransactions(rows),
        coverage: coverageForTransactions(rows),
      };
    })
    .sort((a, b) => b.totalAmountAud - a.totalAmountAud);
}
